import type { TypedSupabaseClient } from '../client';
import { orContainsAnyColumn } from './postgrest-search';
import type {
  IOnlineCustomerRepository,
  IOrderAttributionRepository,
  IShipmentRepository,
  IStorefrontCheckoutRepository,
  IStorefrontCustomerAccountRepository,
  IStorefrontAnalyticsRepository,
  OnlineCustomer,
  CreateOnlineCustomerInput,
  UpdateOnlineCustomerInput,
  OnlineCustomerFilters,
  OrderAttribution,
  CreateOrderAttributionInput,
  Shipment,
  CreateShipmentInput,
  UpdateShipmentInput,
  ShipmentStatus,
  CheckoutSubmitInput,
  CheckoutSubmitResult,
  StorePaymentMethod,
  StorefrontOnlinePaymentStart,
  StorefrontPaymentOutcome,
  StorefrontPaymentStatus,
  StorefrontPaymentVerifyResult,
  SaveStorefrontAccountProfileInput,
  StorefrontAccountOrder,
  StorefrontAccountOrderLine,
  StorefrontAnalytics,
  StorefrontAnalyticsRange,
  StorefrontTraffic,
  StorefrontLiveActivity,
  StorefrontViewedProductRow,
  StorefrontOrdersPoint,
  GovernorateRollup,
  TopProductRollup,
  AttributionRollup,
  CityRollup,
} from '../../../interfaces/src';
import { rollupByGovernorate } from '@erp/domain/storefront';
import { fetchAllRows } from './fetch-all-rows';

// Cast helper that preserves `this` binding — calling `client.from(...)` keeps
// the internal context Supabase needs. Returning just `client.from` would
// detach `this` and hang the request.
const anyFrom = (client: TypedSupabaseClient) => (table: string) =>
  (client as unknown as { from: (t: string) => any }).from(table);

// Narrow rpc cast (generated types are stale) — same idiom as facebook-pixels.
const anyRpc =
  (client: TypedSupabaseClient) =>
  (fn: string, args: Record<string, unknown>) =>
    (
      client as unknown as {
        rpc: (
          f: string,
          a: Record<string, unknown>,
        ) => Promise<{ data: any; error: any }>;
      }
    ).rpc(fn, args);

// ============================================================
// ONLINE CUSTOMERS
// ============================================================

export class SupabaseOnlineCustomerRepository implements IOnlineCustomerRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: OnlineCustomerFilters): Promise<OnlineCustomer[]> {
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) => {
      let q = anyFrom(this.supabase)('online_customers').select('*');
      if (filters?.isBlocked !== undefined)
        q = q.eq('is_blocked', filters.isBlocked);
      if (filters?.city) q = q.eq('city', filters.city);
      if (filters?.isLinked === true)
        q = q.not('linked_customer_id', 'is', null);
      if (filters?.isLinked === false) q = q.is('linked_customer_id', null);
      if (filters?.isRepeat === true) q = q.gte('total_orders', 2);
      if (filters?.isRepeat === false) q = q.lt('total_orders', 2);
      if (filters?.search) {
        q = q.or(
          orContainsAnyColumn(
            ['phone', 'email', 'full_name', 'first_name', 'last_name'],
            filters.search,
          ),
        );
      }
      return q
        .order('last_order_at', {
          ascending: false,
          nullsFirst: false,
        })
        .order('id', { ascending: false })
        .range(from, to);
    });
    return rows.map(mapOnlineCustomer);
  }

  async getById(id: string): Promise<OnlineCustomer | null> {
    const { data, error } = await anyFrom(this.supabase)('online_customers')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data ? mapOnlineCustomer(data) : null;
  }

  async getByPhone(phone: string): Promise<OnlineCustomer | null> {
    const { data, error } = await anyFrom(this.supabase)('online_customers')
      .select('*')
      .eq('phone', normalizePhone(phone))
      .maybeSingle();
    if (error) throw error;
    return data ? mapOnlineCustomer(data) : null;
  }

  async create(input: CreateOnlineCustomerInput): Promise<OnlineCustomer> {
    const { data, error } = await anyFrom(this.supabase)('online_customers')
      .insert({
        phone: normalizePhone(input.phone),
        email: input.email ?? null,
        first_name: input.firstName ?? null,
        last_name: input.lastName ?? null,
        full_name: input.fullName ?? joinName(input.firstName, input.lastName),
        governorate: input.governorate ?? null,
        city: input.city ?? null,
        primary_city: input.city ?? null,
        address: input.address ?? null,
        preferred_language: input.preferredLanguage ?? 'fr',
        notes: input.notes ?? null,
        first_utm_source: input.firstUtmSource ?? null,
        first_utm_campaign: input.firstUtmCampaign ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return mapOnlineCustomer(data);
  }

  async update(
    id: string,
    input: UpdateOnlineCustomerInput,
  ): Promise<OnlineCustomer> {
    const map: Record<string, string> = {
      phone: 'phone',
      email: 'email',
      firstName: 'first_name',
      lastName: 'last_name',
      fullName: 'full_name',
      governorate: 'governorate',
      city: 'city',
      address: 'address',
      preferredLanguage: 'preferred_language',
      isBlocked: 'is_blocked',
      blockReason: 'block_reason',
      linkedCustomerId: 'linked_customer_id',
      notes: 'notes',
    };
    const patch: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input)) {
      if (v === undefined) continue;
      const col = map[k];
      if (col)
        patch[col] =
          k === 'phone' && typeof v === 'string' ? normalizePhone(v) : v;
    }
    if (input.city !== undefined) patch['primary_city'] = input.city;
    if (input.isBlocked === true)
      patch['blocked_at'] = new Date().toISOString();
    if (input.isBlocked === false) patch['blocked_at'] = null;

    const { data, error } = await anyFrom(this.supabase)('online_customers')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapOnlineCustomer(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('online_customers')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  async block(id: string, reason: string): Promise<OnlineCustomer> {
    return this.update(id, { isBlocked: true, blockReason: reason });
  }

  async unblock(id: string): Promise<OnlineCustomer> {
    return this.update(id, { isBlocked: false, blockReason: null });
  }

  async link(id: string, customerId: string): Promise<OnlineCustomer> {
    return this.update(id, { linkedCustomerId: customerId });
  }

  async unlink(id: string): Promise<OnlineCustomer> {
    return this.update(id, { linkedCustomerId: null });
  }

  async recomputeAggregates(id: string): Promise<OnlineCustomer> {
    const { data: orders, error } = await anyFrom(this.supabase)(
      'sales_documents',
    )
      .select('total_ttc, doc_date, created_at')
      .eq('online_customer_id', id)
      .eq('doc_type', 'order');
    if (error) throw error;
    const list = (orders ?? []) as Array<{
      total_ttc: number;
      doc_date: string;
      created_at: string;
    }>;
    const totalOrders = list.length;
    const totalSpent = list.reduce(
      (sum, r) => sum + Number(r.total_ttc || 0),
      0,
    );
    const firstOrderAt = list.length
      ? list.reduce(
          (min, r) => (r.created_at < min ? r.created_at : min),
          list[0]!.created_at,
        )
      : null;
    const lastOrderAt = list.length
      ? list.reduce(
          (max, r) => (r.created_at > max ? r.created_at : max),
          list[0]!.created_at,
        )
      : null;

    const { data, error: upErr } = await anyFrom(this.supabase)(
      'online_customers',
    )
      .update({
        total_orders: totalOrders,
        total_spent: totalSpent,
        first_order_at: firstOrderAt,
        last_order_at: lastOrderAt,
      })
      .eq('id', id)
      .select()
      .single();
    if (upErr) throw upErr;
    return mapOnlineCustomer(data);
  }
}

function normalizePhone(phone: string): string {
  return phone.replace(/[^\d+]/g, '');
}

function joinName(first?: string | null, last?: string | null): string | null {
  const parts = [first, last].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
}

function mapOnlineCustomer(row: Record<string, unknown>): OnlineCustomer {
  return {
    id: row['id'] as string,
    phone: row['phone'] as string,
    email: (row['email'] as string) ?? null,
    firstName: (row['first_name'] as string) ?? null,
    lastName: (row['last_name'] as string) ?? null,
    fullName: (row['full_name'] as string) ?? null,
    governorate: (row['governorate'] as string) ?? null,
    city: (row['city'] as string) ?? null,
    address: (row['address'] as string) ?? null,
    primaryCity: (row['primary_city'] as string) ?? null,
    preferredLanguage: (row['preferred_language'] as string) ?? 'fr',
    totalOrders: (row['total_orders'] as number) ?? 0,
    totalSpent: Number(row['total_spent'] ?? 0),
    firstOrderAt: (row['first_order_at'] as string) ?? null,
    lastOrderAt: (row['last_order_at'] as string) ?? null,
    isBlocked: (row['is_blocked'] as boolean) ?? false,
    blockReason: (row['block_reason'] as string) ?? null,
    blockedAt: (row['blocked_at'] as string) ?? null,
    linkedCustomerId: (row['linked_customer_id'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    firstUtmSource: (row['first_utm_source'] as string) ?? null,
    firstUtmCampaign: (row['first_utm_campaign'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

// ============================================================
// ORDER ATTRIBUTION
// ============================================================

export class SupabaseOrderAttributionRepository implements IOrderAttributionRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async getBySalesDocumentId(
    salesDocumentId: string,
  ): Promise<OrderAttribution | null> {
    const { data, error } = await anyFrom(this.supabase)('order_attribution')
      .select('*')
      .eq('sales_document_id', salesDocumentId)
      .maybeSingle();
    if (error) throw error;
    return data ? mapAttribution(data) : null;
  }

  async create(input: CreateOrderAttributionInput): Promise<OrderAttribution> {
    const { data, error } = await anyFrom(this.supabase)('order_attribution')
      .insert({
        sales_document_id: input.salesDocumentId,
        utm_source: input.utmSource ?? null,
        utm_medium: input.utmMedium ?? null,
        utm_campaign: input.utmCampaign ?? null,
        utm_content: input.utmContent ?? null,
        utm_term: input.utmTerm ?? null,
        landing_page: input.landingPage ?? null,
        referrer: input.referrer ?? null,
        ad_platform: input.adPlatform ?? null,
        ad_id: input.adId ?? null,
        adset_id: input.adsetId ?? null,
        campaign_id: input.campaignId ?? null,
        ad_name: input.adName ?? null,
        adset_name: input.adsetName ?? null,
        campaign_name: input.campaignName ?? null,
        user_agent: input.userAgent ?? null,
        ip_address: input.ipAddress ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return mapAttribution(data);
  }
}

function mapAttribution(row: Record<string, unknown>): OrderAttribution {
  return {
    id: row['id'] as string,
    salesDocumentId: row['sales_document_id'] as string,
    utmSource: (row['utm_source'] as string) ?? null,
    utmMedium: (row['utm_medium'] as string) ?? null,
    utmCampaign: (row['utm_campaign'] as string) ?? null,
    utmContent: (row['utm_content'] as string) ?? null,
    utmTerm: (row['utm_term'] as string) ?? null,
    landingPage: (row['landing_page'] as string) ?? null,
    referrer: (row['referrer'] as string) ?? null,
    adPlatform: (row['ad_platform'] as string) ?? null,
    adId: (row['ad_id'] as string) ?? null,
    adsetId: (row['adset_id'] as string) ?? null,
    campaignId: (row['campaign_id'] as string) ?? null,
    adName: (row['ad_name'] as string) ?? null,
    adsetName: (row['adset_name'] as string) ?? null,
    campaignName: (row['campaign_name'] as string) ?? null,
    userAgent: (row['user_agent'] as string) ?? null,
    ipAddress: (row['ip_address'] as string) ?? null,
    capturedAt: row['captured_at'] as string,
    createdAt: row['created_at'] as string,
  };
}

// ============================================================
// SHIPMENTS
// ============================================================

export class SupabaseShipmentRepository implements IShipmentRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async listBySalesDocumentId(salesDocumentId: string): Promise<Shipment[]> {
    const { data, error } = await anyFrom(this.supabase)('shipments')
      .select('*')
      .eq('sales_document_id', salesDocumentId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapShipment);
  }

  async listBySalesDocumentIds(
    salesDocumentIds: string[],
  ): Promise<Shipment[]> {
    if (salesDocumentIds.length === 0) return [];
    const { data, error } = await anyFrom(this.supabase)('shipments')
      .select('*')
      .in('sales_document_id', salesDocumentIds)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapShipment);
  }

  async getById(id: string): Promise<Shipment | null> {
    const { data, error } = await anyFrom(this.supabase)('shipments')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data ? mapShipment(data) : null;
  }

  async create(input: CreateShipmentInput): Promise<Shipment> {
    const { data, error } = await anyFrom(this.supabase)('shipments')
      .insert({
        sales_document_id: input.salesDocumentId,
        fulfillment_store_id: input.fulfillmentStoreId ?? null,
        courier_name: input.courierName ?? null,
        tracking_number: input.trackingNumber ?? null,
        shipment_status: input.shipmentStatus ?? 'pending',
        expected_delivery_date: input.expectedDeliveryDate ?? null,
        delivery_fee: input.deliveryFee ?? 0,
        return_fee: input.returnFee ?? 0,
        delivery_address: input.deliveryAddress ?? null,
        delivery_city: input.deliveryCity ?? null,
        delivery_phone: input.deliveryPhone ?? null,
        notes: input.notes ?? null,
        provider_id: input.providerId ?? null,
        provider_external_id: input.providerExternalId ?? null,
        provider_status_code: input.providerStatusCode ?? null,
        cod_amount: input.codAmount ?? 0,
        package_size: input.packageSize ?? 1,
        carrier_code: input.carrierCode ?? null,
        label_url: input.labelUrl ?? null,
        carrier_status_raw: input.carrierStatusRaw ?? null,
        cod_currency: input.codCurrency ?? 'TND',
      })
      .select()
      .single();
    if (error) throw error;
    return mapShipment(data);
  }

  async update(id: string, input: UpdateShipmentInput): Promise<Shipment> {
    const map: Record<string, string> = {
      courierName: 'courier_name',
      fulfillmentStoreId: 'fulfillment_store_id',
      trackingNumber: 'tracking_number',
      shipmentStatus: 'shipment_status',
      expectedDeliveryDate: 'expected_delivery_date',
      deliveryFee: 'delivery_fee',
      returnFee: 'return_fee',
      deliveryAddress: 'delivery_address',
      deliveryCity: 'delivery_city',
      deliveryPhone: 'delivery_phone',
      notes: 'notes',
      providerId: 'provider_id',
      providerExternalId: 'provider_external_id',
      providerStatusCode: 'provider_status_code',
      codAmount: 'cod_amount',
      packageSize: 'package_size',
      lastSyncedAt: 'last_synced_at',
      carrierCode: 'carrier_code',
      labelUrl: 'label_url',
      carrierStatusRaw: 'carrier_status_raw',
      codCurrency: 'cod_currency',
    };
    const patch: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input)) {
      if (v === undefined) continue;
      const col = map[k];
      if (col) patch[col] = v;
    }
    if (input.shipmentStatus === 'dispatched')
      patch['dispatched_at'] = new Date().toISOString();
    if (input.shipmentStatus === 'delivered')
      patch['delivered_at'] = new Date().toISOString();
    if (input.shipmentStatus === 'returned')
      patch['returned_at'] = new Date().toISOString();

    const { data, error } = await anyFrom(this.supabase)('shipments')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapShipment(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('shipments')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }
}

function mapShipment(row: Record<string, unknown>): Shipment {
  return {
    id: row['id'] as string,
    salesDocumentId: row['sales_document_id'] as string,
    fulfillmentStoreId: (row['fulfillment_store_id'] as string | null) ?? null,
    courierName: (row['courier_name'] as string) ?? null,
    trackingNumber: (row['tracking_number'] as string) ?? null,
    shipmentStatus: (row['shipment_status'] as ShipmentStatus) ?? 'pending',
    dispatchedAt: (row['dispatched_at'] as string) ?? null,
    deliveredAt: (row['delivered_at'] as string) ?? null,
    returnedAt: (row['returned_at'] as string) ?? null,
    expectedDeliveryDate: (row['expected_delivery_date'] as string) ?? null,
    deliveryFee: Number(row['delivery_fee'] ?? 0),
    returnFee: Number(row['return_fee'] ?? 0),
    deliveryAddress: (row['delivery_address'] as string) ?? null,
    deliveryCity: (row['delivery_city'] as string) ?? null,
    deliveryPhone: (row['delivery_phone'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    createdBy: (row['created_by'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    providerId: (row['provider_id'] as string | null) ?? null,
    providerExternalId: (row['provider_external_id'] as string | null) ?? null,
    providerStatusCode:
      row['provider_status_code'] == null
        ? null
        : Number(row['provider_status_code']),
    codAmount: Number(row['cod_amount'] ?? 0),
    packageSize: Number(row['package_size'] ?? 1),
    lastSyncedAt: (row['last_synced_at'] as string | null) ?? null,
    carrierCode: (row['carrier_code'] as string | null) ?? null,
    labelUrl: (row['label_url'] as string | null) ?? null,
    carrierStatusRaw: (row['carrier_status_raw'] as string | null) ?? null,
    codCurrency: (row['cod_currency'] as string | null) ?? 'TND',
  };
}

// ============================================================
// STOREFRONT CHECKOUT (public submission)
// ============================================================

export class SupabaseStorefrontCheckoutRepository implements IStorefrontCheckoutRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async submit(input: CheckoutSubmitInput): Promise<CheckoutSubmitResult> {
    const rpcResult = await this.submitViaRpc(input);
    if (rpcResult) return rpcResult;

    const { customer, items, attribution, notes } = input;
    if (!items.length) throw new Error('empty_cart');

    // 1. Resolve storefront products + articles (price, VAT, name)
    const productIds = items.map((it) => it.storefrontProductId);
    const { data: products, error: productsErr } = await anyFrom(this.supabase)(
      'storefront_products',
    )
      .select(
        'id, tenant_id, article_id, price_override, name_fr, name_ar, name_en, articles:storefront_catalog_articles(name, sale_price_ttc, vat_rate)',
      )
      .in('id', productIds)
      .eq('is_published', true);
    if (productsErr) throw productsErr;
    const productList = (products ?? []) as any[];

    if (productList.length !== productIds.length) {
      throw new Error('product_not_available');
    }
    const tenantId = productList[0]?.tenant_id as string | undefined;
    if (!tenantId) throw new Error('tenant_not_found');
    if (productList.some((p) => p.tenant_id !== tenantId)) {
      throw new Error('product_not_available');
    }

    // 2. Compute line totals and grand totals
    type Line = {
      storefrontProductId: string;
      articleId: string;
      quantity: number;
      unitPriceTtc: number;
      unitPriceHt: number;
      vatRate: number;
      description: string;
      totalHt: number;
      totalTtc: number;
    };
    const lines: Line[] = items.map((item) => {
      const p = productList.find(
        (x: any) => x.id === item.storefrontProductId,
      )!;
      const article = p.articles ?? {};
      // Defense-in-depth (the RPC path validates server-side): quantities must
      // be positive bounded integers or the totals can go negative/absurd.
      const quantity = Math.trunc(Number(item.quantity));
      if (!Number.isFinite(quantity) || quantity < 1 || quantity > 10_000) {
        throw new Error('invalid_quantity');
      }
      const priceTtc =
        p.price_override != null
          ? Number(p.price_override)
          : Number(article.sale_price_ttc || 0);
      const vatRate = Number(article.vat_rate || 0);
      const priceHt = vatRate > 0 ? priceTtc / (1 + vatRate / 100) : priceTtc;
      const totalTtc = round3(priceTtc * quantity);
      const totalHt = round3(priceHt * quantity);
      const name = p.name_fr ?? p.name_en ?? article.name ?? '';
      return {
        storefrontProductId: p.id,
        articleId: p.article_id,
        quantity,
        unitPriceTtc: priceTtc,
        unitPriceHt: priceHt,
        vatRate,
        description: name,
        totalHt,
        totalTtc,
      };
    });

    const totalHt = round3(lines.reduce((s, l) => s + l.totalHt, 0));
    const itemsTtc = round3(lines.reduce((s, l) => s + l.totalTtc, 0));
    const totalVat = round3(itemsTtc - totalHt);

    // Pull the per-store delivery fee. It's added to every storefront order
    // and frozen on the row, so changing the setting later doesn't alter
    // historical orders. The items subtotal is passed so the free-shipping
    // threshold can waive the fee (same rule as the RPC path).
    const deliveryFee = await this.fetchStoreDeliveryFee(tenantId, itemsTtc);
    const totalTtc = round3(itemsTtc + deliveryFee);

    // 3. Upsert online customer by phone
    const phone = normalizePhone(customer.phone);
    let existing: OnlineCustomer | null = null;
    {
      const { data, error } = await anyFrom(this.supabase)('online_customers')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('phone', phone)
        .maybeSingle();
      if (error) throw error;
      if (data) existing = mapOnlineCustomer(data);
    }

    if (existing?.isBlocked) throw new Error('customer_blocked');

    // Effective payment method. The anon-readable columns only say whether
    // the gateway is enabled (tokens stay server-side); the RPC path — and,
    // later, storefront-flouci-pay — re-validate the full configuration.
    let paymentMethod: StorePaymentMethod = 'cod';
    if (input.paymentMethod === 'flouci') {
      try {
        const { data: provider } = await anyFrom(this.supabase)(
          'store_payment_providers',
        )
          .select('code, is_enabled')
          .eq('tenant_id', tenantId)
          .eq('code', 'flouci')
          .eq('is_enabled', true)
          .maybeSingle();
        if (provider) paymentMethod = 'flouci';
      } catch {
        /* keep COD — an order must never be lost over a gateway check */
      }
    }

    let onlineCustomerId: string;
    if (existing) {
      const patch: Record<string, unknown> = {
        total_orders: existing.totalOrders + 1,
        total_spent: Number(existing.totalSpent) + totalTtc,
        last_order_at: new Date().toISOString(),
      };
      if (!existing.firstOrderAt)
        patch['first_order_at'] = new Date().toISOString();
      if (customer.fullName) patch['full_name'] = customer.fullName;
      else if (customer.firstName || customer.lastName)
        patch['full_name'] = joinName(customer.firstName, customer.lastName);
      if (customer.firstName) patch['first_name'] = customer.firstName;
      if (customer.lastName) patch['last_name'] = customer.lastName;
      if (customer.email) patch['email'] = customer.email;
      if (customer.governorate) patch['governorate'] = customer.governorate;
      if (customer.city) {
        patch['city'] = customer.city;
        patch['primary_city'] = customer.city;
      }
      if (customer.address) patch['address'] = customer.address;
      if (customer.language) patch['preferred_language'] = customer.language;
      const { error } = await anyFrom(this.supabase)('online_customers')
        .update(patch)
        .eq('id', existing.id);
      if (error) throw error;
      onlineCustomerId = existing.id;
    } else {
      const { data, error } = await anyFrom(this.supabase)('online_customers')
        .insert({
          phone,
          tenant_id: tenantId,
          email: customer.email ?? null,
          first_name: customer.firstName ?? null,
          last_name: customer.lastName ?? null,
          full_name:
            customer.fullName ??
            joinName(customer.firstName, customer.lastName),
          governorate: customer.governorate ?? null,
          city: customer.city ?? null,
          primary_city: customer.city ?? null,
          address: customer.address ?? null,
          preferred_language: customer.language ?? 'fr',
          total_orders: 1,
          total_spent: totalTtc,
          first_order_at: new Date().toISOString(),
          last_order_at: new Date().toISOString(),
          first_utm_source: attribution?.utmSource ?? null,
          first_utm_campaign: attribution?.utmCampaign ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      onlineCustomerId = (data as Record<string, unknown>)['id'] as string;
    }

    // 4. Insert sales_documents (order). Once the DB migration is applied,
    // the trigger overwrites `number` atomically. Until then, this fallback
    // removes the broken nextval_safe call and avoids reusing the first number.
    let orderRow: Record<string, unknown> | null = null;
    let orderErr: unknown = null;
    // payment_status is derived by the DB trigger (never client-set — a
    // storefront order can't be born 'paid'). When the payments migration
    // isn't applied yet (42703 undefined column), retry without the field
    // so a frontend deploy ahead of the DB can never break checkout.
    let includePaymentMethod = true;
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const orderNumber = await this.buildNextOrderNumber(tenantId, attempt);
      const { data, error } = await anyFrom(this.supabase)('sales_documents')
        .insert({
          doc_type: 'order',
          tenant_id: tenantId,
          number: orderNumber,
          customer_source: 'online',
          client_id: null,
          online_customer_id: onlineCustomerId,
          channel: 'storefront',
          storefront_order_status: 'pending',
          doc_date: new Date().toISOString().slice(0, 10),
          total_ht: totalHt,
          total_vat: totalVat,
          total_ttc: totalTtc,
          total_discount: 0,
          withholding_amount: 0,
          delivery_fee: deliveryFee,
          net_to_pay: totalTtc,
          notes: notes ?? null,
          ...(includePaymentMethod ? { payment_method: paymentMethod } : {}),
        })
        .select()
        .single();

      if (!error) {
        orderRow = data as Record<string, unknown>;
        orderErr = null;
        break;
      }
      orderErr = error;
      if (includePaymentMethod && isUndefinedColumn(error)) {
        includePaymentMethod = false;
        paymentMethod = 'cod';
        continue;
      }
      if (!isUniqueViolation(error)) break;
    }
    if (orderErr || !orderRow)
      throw orderErr ?? new Error('order_create_failed');
    const orderId = (orderRow as Record<string, unknown>)['id'] as string;
    const orderNumber = (orderRow as Record<string, unknown>)['number'] as
      | string
      | undefined;
    if (!orderNumber) throw new Error('order_number_failed');

    // 6. Insert lines
    const lineRows = lines.map((l, i) => ({
      document_id: orderId,
      tenant_id: tenantId,
      line_number: i + 1,
      article_id: l.articleId,
      description: l.description,
      quantity: l.quantity,
      unit_price_ht: l.unitPriceHt,
      discount_pct: 0,
      vat_rate: l.vatRate,
      total_ht: l.totalHt,
      total_ttc: l.totalTtc,
    }));
    const { error: linesErr } = await anyFrom(this.supabase)(
      'sales_document_lines',
    ).insert(lineRows);
    if (linesErr) throw linesErr;

    // 7. Insert order attribution (always insert, even if empty — for funnel tracking)
    if (attribution) {
      await anyFrom(this.supabase)('order_attribution').insert({
        sales_document_id: orderId,
        tenant_id: tenantId,
        utm_source: attribution.utmSource ?? null,
        utm_medium: attribution.utmMedium ?? null,
        utm_campaign: attribution.utmCampaign ?? null,
        utm_content: attribution.utmContent ?? null,
        utm_term: attribution.utmTerm ?? null,
        landing_page: attribution.landingPage ?? null,
        referrer: attribution.referrer ?? null,
        ad_platform: attribution.adPlatform ?? null,
        ad_id: attribution.adId ?? null,
        adset_id: attribution.adsetId ?? null,
        campaign_id: attribution.campaignId ?? null,
        ad_name: attribution.adName ?? null,
        adset_name: attribution.adsetName ?? null,
        campaign_name: attribution.campaignName ?? null,
      });
    }

    // 8. Notify admin + recover any matching abandoned cart
    const displayName =
      customer.fullName ??
      joinName(customer.firstName, customer.lastName) ??
      phone;
    try {
      await anyFrom(this.supabase)('admin_notifications').insert({
        kind: 'order_new',
        tenant_id: tenantId,
        title: `Nouvelle commande ${orderNumber}`,
        message: `${displayName} — ${round3(totalTtc).toFixed(3)} TND · ${lines.length} article(s)`,
        link_path: `/storefront/orders/${orderId}`,
        entity_type: 'sales_document',
        entity_id: orderId,
        metadata: {
          orderNumber,
          totalTtc,
          customerPhone: phone,
          customerName: displayName,
          itemCount: lines.length,
        },
      });
    } catch {
      /* best-effort; don't fail the order */
    }

    try {
      await anyFrom(this.supabase)('abandoned_carts')
        .update({ status: 'recovered', recovered_sales_document_id: orderId })
        .eq('tenant_id', tenantId)
        .eq('phone', phone)
        .eq('status', 'active');
    } catch {
      /* best-effort */
    }

    // 9. Optional thank-you page content snapshot
    const { data: thankYou } = await anyFrom(this.supabase)('store_pages')
      .select(
        'title_fr, title_ar, title_en, content_fr, content_ar, content_en',
      )
      .eq('tenant_id', tenantId)
      .eq('page_key', 'thank_you')
      .maybeSingle();

    return {
      orderId,
      orderNumber,
      onlineCustomerId,
      totalTtc,
      paymentMethod,
      thankYouContent: thankYou
        ? {
            titleFr:
              (thankYou as Record<string, string | null>)['title_fr'] ?? null,
            titleAr:
              (thankYou as Record<string, string | null>)['title_ar'] ?? null,
            titleEn:
              (thankYou as Record<string, string | null>)['title_en'] ?? null,
            contentFr:
              (thankYou as Record<string, string | null>)['content_fr'] ?? null,
            contentAr:
              (thankYou as Record<string, string | null>)['content_ar'] ?? null,
            contentEn:
              (thankYou as Record<string, string | null>)['content_en'] ?? null,
          }
        : undefined,
    };
  }

  /** Reads the configured delivery fee from store_settings and waives it when
   *  the free-shipping threshold is reached by the items subtotal (keep in
   *  sync with resolveDeliveryFee() in @erp/domain/storefront — inlined here
   *  because not every adapter consumer aliases that lib). Returns 0 if the
   *  table is empty or the column is null — never throws, since checkout must
   *  not fail just because the fee is missing. */
  private async fetchStoreDeliveryFee(
    tenantId: string,
    itemsSubtotalTtc: number,
  ): Promise<number> {
    try {
      let { data, error } = await anyFrom(this.supabase)('store_settings')
        .select('delivery_fee, free_shipping_threshold')
        .eq('tenant_id', tenantId)
        .limit(1)
        .maybeSingle();
      if (error) {
        // free_shipping_threshold may not be migrated yet (42703) — retry with
        // the legacy column only rather than silently waiving the fee.
        ({ data, error } = await anyFrom(this.supabase)('store_settings')
          .select('delivery_fee')
          .eq('tenant_id', tenantId)
          .limit(1)
          .maybeSingle());
        if (error) return 0;
      }
      const row = data as {
        delivery_fee?: number | null;
        free_shipping_threshold?: number | null;
      } | null;
      const fee = Number(row?.delivery_fee ?? 0);
      if (!Number.isFinite(fee) || fee <= 0) return 0;
      const threshold = Number(row?.free_shipping_threshold ?? 0);
      if (
        Number.isFinite(threshold) &&
        threshold > 0 &&
        itemsSubtotalTtc >= threshold
      )
        return 0;
      return round3(fee);
    } catch {
      return 0;
    }
  }

  private async buildNextOrderNumber(
    tenantId: string,
    offset: number,
  ): Promise<string> {
    const year = new Date().getFullYear();
    const prefix = `CMD-${year}-`;
    const { data } = await anyFrom(this.supabase)('sales_documents')
      .select('number')
      .eq('tenant_id', tenantId)
      .eq('doc_type', 'order')
      .like('number', `${prefix}%`)
      .order('number', { ascending: false })
      .limit(1);
    const last = Array.isArray(data)
      ? (data[0]?.number as string | undefined)
      : undefined;
    const parsed = last?.match(/^CMD-\d{4}-(\d+)$/);
    const next = (parsed ? Number(parsed[1]) : 0) + 1 + offset;
    return `${prefix}${String(next).padStart(4, '0')}`;
  }

  private async submitViaRpc(
    input: CheckoutSubmitInput,
  ): Promise<CheckoutSubmitResult | null> {
    const client = this.supabase as unknown as {
      rpc: (
        name: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: unknown }>;
    };
    const { data, error } = await client.rpc('submit_storefront_checkout', {
      p_customer: input.customer,
      p_items: input.items,
      p_attribution: input.attribution ?? {},
      p_notes: input.notes ?? null,
      // The RPC validates server-side that the tenant actually has the
      // gateway enabled+configured; anything else falls back to COD.
      p_payment_method: input.paymentMethod === 'flouci' ? 'flouci' : 'cod',
    });
    if (error) {
      if (isMissingRpc(error)) return null;
      throw normalizeSupabaseError(error);
    }
    return mapCheckoutSubmitResult(data);
  }

  // ---- Online payment (Flouci via edge functions) ----
  // The edge functions hold all the sensitive work: merchant tokens from
  // store_payment_providers (service role), amount from the order row.
  // The storefront only ever sends the order id.

  async startOnlinePayment(
    orderId: string,
  ): Promise<StorefrontOnlinePaymentStart> {
    const { data, error } = await this.supabase.functions.invoke(
      'storefront-flouci-pay',
      {
        body: { orderId },
      },
    );
    if (error) throw await paymentEdgeError(error, 'payment_start_failed');
    const res = data as { link?: string; paymentId?: string } | null;
    if (!res?.link || !res.paymentId)
      throw new Error('payment_start_empty_response');
    return { link: res.link, paymentId: res.paymentId };
  }

  async verifyOnlinePayment(
    orderId: string,
  ): Promise<StorefrontPaymentVerifyResult> {
    const { data, error } = await this.supabase.functions.invoke(
      'storefront-flouci-verify',
      {
        body: { orderId },
      },
    );
    if (error) throw await paymentEdgeError(error, 'payment_verify_failed');
    const res = data as {
      outcome?: string;
      paymentStatus?: string | null;
    } | null;
    const outcomes: StorefrontPaymentOutcome[] = [
      'paid',
      'already_paid',
      'pending',
      'failed',
      'expired',
      'none',
    ];
    const statuses: StorefrontPaymentStatus[] = [
      'unpaid',
      'pending',
      'paid',
      'failed',
    ];
    return {
      outcome: outcomes.find((o) => o === res?.outcome) ?? 'none',
      paymentStatus: statuses.find((s) => s === res?.paymentStatus) ?? null,
    };
  }
}

/** Surface the edge function's JSON error code (same idiom as
 *  flouci-payment-service): `payment_not_configured`, `already_paid`,
 *  `flouci_unavailable`, … become the Error message the UI switches on. */
async function paymentEdgeError(
  error: unknown,
  fallback: string,
): Promise<Error> {
  let code = fallback;
  try {
    const ctx = (
      error as { context?: { json?: () => Promise<{ error?: string }> } }
    ).context;
    if (ctx?.json) {
      const body = await ctx.json();
      if (body?.error) code = body.error;
    } else if (error && typeof error === 'object' && 'message' in error) {
      code = String((error as { message: string }).message);
    }
  } catch {
    /* keep fallback */
  }
  const err = new Error(code);
  (err as Error & { code?: string }).code = code;
  return err;
}

// ============================================================
// STOREFRONT CUSTOMER ACCOUNT
// ============================================================

export class SupabaseStorefrontCustomerAccountRepository implements IStorefrontCustomerAccountRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async getProfileByEmail(
    email: string,
    tenantId?: string,
  ): Promise<OnlineCustomer | null> {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) return null;

    let query = anyFrom(this.supabase)('online_customers')
      .select('*')
      .ilike('email', normalizedEmail)
      .order('last_order_at', { ascending: false, nullsFirst: false });
    if (tenantId) query = query.eq('tenant_id', tenantId);

    const { data, error } = await query.limit(1).maybeSingle();
    if (error) throw error;
    return data ? mapOnlineCustomer(data) : null;
  }

  async saveProfile(
    input: SaveStorefrontAccountProfileInput,
  ): Promise<OnlineCustomer> {
    const email = normalizeEmail(input.email);
    if (!email) throw new Error('email_required');

    const existing = await this.getProfileByEmail(email, input.tenantId);
    if (existing) {
      const { data, error } = await anyFrom(this.supabase)('online_customers')
        .update({
          email,
          full_name: input.fullName ?? existing.fullName,
          phone: input.phone ? normalizePhone(input.phone) : existing.phone,
          city: input.city ?? existing.city,
          primary_city: input.city ?? existing.primaryCity,
          address: input.address ?? existing.address,
        })
        .eq('id', existing.id)
        .select()
        .single();
      if (error) throw error;
      return mapOnlineCustomer(data);
    }

    if (!input.phone) throw new Error('phone_required');
    const { data, error } = await anyFrom(this.supabase)('online_customers')
      .insert({
        tenant_id: input.tenantId,
        phone: normalizePhone(input.phone),
        email,
        full_name: input.fullName ?? null,
        city: input.city ?? null,
        primary_city: input.city ?? null,
        address: input.address ?? null,
        preferred_language: 'fr',
      })
      .select()
      .single();
    if (error) throw error;
    return mapOnlineCustomer(data);
  }

  async listOrdersByEmail(
    email: string,
    tenantId?: string,
  ): Promise<StorefrontAccountOrder[]> {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) return [];

    let customerQuery = anyFrom(this.supabase)('online_customers')
      .select('id')
      .ilike('email', normalizedEmail);
    if (tenantId) customerQuery = customerQuery.eq('tenant_id', tenantId);

    const { data: customers, error: customersError } = await customerQuery;
    if (customersError) throw customersError;

    const customerIds = ((customers ?? []) as Array<{ id: string }>).map(
      (customer) => customer.id,
    );
    if (customerIds.length === 0) return [];

    // Compte client : `authorized_*` de bout en bout. La vue rend la commande
    // au client en ligne via sa branche `is_online_customer_session()`, et le
    // masquage y retire `purchase_price` des lignes — ce que la table de base
    // ne faisait pas.
    let orderQuery = anyFrom(this.supabase)('authorized_sales_documents')
      .select(
        'id, number, doc_date, storefront_order_status, total_ttc, delivery_fee, sales_document_lines:authorized_sales_document_lines(id, description, quantity, total_ttc, articles:authorized_articles(image_url))',
      )
      .eq('channel', 'storefront')
      .eq('doc_type', 'order')
      .in('online_customer_id', customerIds);
    if (tenantId) orderQuery = orderQuery.eq('tenant_id', tenantId);

    const { data, error } = await orderQuery.order('created_at', {
      ascending: false,
    });
    if (error) throw error;

    return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
      id: row['id'] as string,
      orderNumber: row['number'] as string,
      docDate: row['doc_date'] as string,
      status:
        (row['storefront_order_status'] as StorefrontAccountOrder['status']) ??
        null,
      totalTtc: Number(row['total_ttc'] ?? 0),
      deliveryFee: Number(row['delivery_fee'] ?? 0),
      lines: (
        ((row['sales_document_lines'] as unknown[]) ?? []) as Array<
          Record<string, unknown>
        >
      ).map(
        (line): StorefrontAccountOrderLine => ({
          id: line['id'] as string,
          description: (line['description'] as string) ?? '',
          imageUrl: readOrderLineArticleImage(line),
          quantity: Number(line['quantity'] ?? 0),
          totalTtc: Number(line['total_ttc'] ?? 0),
        }),
      ),
    }));
  }
}

function readOrderLineArticleImage(
  line: Record<string, unknown>,
): string | null {
  const article = line['articles'];
  if (!article || typeof article !== 'object') return null;
  const imageUrl = (article as Record<string, unknown>)['image_url'];
  return typeof imageUrl === 'string' && imageUrl.trim() ? imageUrl : null;
}

function normalizeEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

function isUniqueViolation(error: unknown): boolean {
  return Boolean(
    error &&
    typeof error === 'object' &&
    'code' in error &&
    error.code === '23505',
  );
}

/** Postgres 42703: a column in the payload doesn't exist (schema behind code). */
function isUndefinedColumn(error: unknown): boolean {
  return Boolean(
    error &&
    typeof error === 'object' &&
    'code' in error &&
    error.code === '42703',
  );
}

function isMissingRpc(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const record = error as Record<string, unknown>;
  const code = String(record['code'] ?? '');
  const message = String(record['message'] ?? record['details'] ?? '');
  return (
    code === 'PGRST202' ||
    code === '42883' ||
    /function .* does not exist|could not find the function/i.test(message)
  );
}

function normalizeSupabaseError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const message = record['message'] ?? record['details'] ?? record['hint'];
    if (typeof message === 'string' && message.trim())
      return new Error(message);
  }
  return new Error(String(error));
}

function mapCheckoutSubmitResult(data: unknown): CheckoutSubmitResult {
  const row = (data ?? {}) as Record<string, unknown>;
  const thankYou = row['thankYouContent'] ?? row['thank_you_content'];
  const rawMethod = row['paymentMethod'] ?? row['payment_method'];
  return {
    orderId: String(row['orderId'] ?? row['order_id'] ?? ''),
    orderNumber: String(row['orderNumber'] ?? row['order_number'] ?? ''),
    onlineCustomerId: String(
      row['onlineCustomerId'] ?? row['online_customer_id'] ?? '',
    ),
    totalTtc: Number(row['totalTtc'] ?? row['total_ttc'] ?? 0),
    // Older RPC versions don't return it — undefined means "assume COD".
    paymentMethod:
      rawMethod === 'flouci'
        ? 'flouci'
        : rawMethod === 'cod'
          ? 'cod'
          : undefined,
    thankYouContent:
      thankYou && typeof thankYou === 'object'
        ? {
            titleFr: ((thankYou as Record<string, unknown>)['titleFr'] ??
              null) as string | null,
            titleAr: ((thankYou as Record<string, unknown>)['titleAr'] ??
              null) as string | null,
            titleEn: ((thankYou as Record<string, unknown>)['titleEn'] ??
              null) as string | null,
            contentFr: ((thankYou as Record<string, unknown>)['contentFr'] ??
              null) as string | null,
            contentAr: ((thankYou as Record<string, unknown>)['contentAr'] ??
              null) as string | null,
            contentEn: ((thankYou as Record<string, unknown>)['contentEn'] ??
              null) as string | null,
          }
        : undefined,
  };
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

// ============================================================
// STOREFRONT ANALYTICS
// ============================================================

export class SupabaseStorefrontAnalyticsRepository implements IStorefrontAnalyticsRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async getAnalytics(
    range: StorefrontAnalyticsRange,
  ): Promise<StorefrontAnalytics> {
    const { from, to } = range;
    const previous = previousWindow(from, to);

    // All storefront orders in range
    const { data: rawOrders } = await anyFrom(this.supabase)('sales_documents')
      .select(
        'id, total_ttc, doc_date, created_at, storefront_order_status, online_customer_id, online_customers(city)',
      )
      .eq('channel', 'storefront')
      .eq('doc_type', 'order')
      .gte('doc_date', from)
      .lte('doc_date', to);
    const orders = (rawOrders ?? []) as any[];

    const totalOrders = orders.length;
    const totalRevenue = orders.reduce(
      (s, o) => s + Number(o.total_ttc || 0),
      0,
    );
    const orderIds = orders.map((o) => o.id as string);
    const deliveredOrders = orders.filter(
      (o) => o.storefront_order_status === 'delivered',
    ).length;

    // Same two figures over the preceding window, so the UI can show a delta
    // on revenue and order count the same way it does on traffic.
    const { data: rawPrevOrders } = await anyFrom(this.supabase)(
      'sales_documents',
    )
      .select('total_ttc')
      .eq('channel', 'storefront')
      .eq('doc_type', 'order')
      .gte('doc_date', previous.from)
      .lte('doc_date', previous.to);
    const prevOrders = (rawPrevOrders ?? []) as any[];
    const previousTotalOrders = prevOrders.length;
    const previousTotalRevenue = prevOrders.reduce(
      (s, o) => s + Number(o.total_ttc || 0),
      0,
    );

    // Carts abandoned in the window. Was hardcoded to 0.
    const { count: abandonedCount } = await anyFrom(this.supabase)(
      'abandoned_carts',
    )
      .select('id', { count: 'exact', head: true })
      .gte('created_at', `${from}T00:00:00`)
      .lte('created_at', `${to}T23:59:59`);
    const abandonedCarts = abandonedCount ?? 0;

    // Traffic: visits, page views, clicks, breakdowns — one RPC. Needs an
    // explicit tenant (a null p_tenant_id raises 22023 'tenant id required'),
    // so resolve it the way every other tenant-scoped repo does.
    //
    // Soft-fails to `null` rather than throwing: an environment whose
    // migrations haven't caught up still gets the order-based half of the
    // report instead of an error page. Logged so a real regression surfaces
    // instead of silently reporting zero traffic.
    let traffic: StorefrontTraffic | null = null;
    const { data: tenantId } = await anyRpc(this.supabase)(
      'current_user_tenant_id',
      {},
    );
    if (!tenantId) {
      console.warn(
        '[storefront-analytics] no tenant resolved — traffic unavailable',
      );
    } else {
      const { data: trafficData, error: trafficError } = await anyRpc(
        this.supabase,
      )('storefront_analytics_traffic', {
        p_tenant_id: tenantId,
        p_from: from,
        p_to: to,
      });
      if (trafficError) {
        console.warn(
          '[storefront-analytics] storefront_analytics_traffic failed:',
          trafficError.message,
        );
      } else if (trafficData) {
        traffic = mapTraffic(trafficData, { from, to, previous });
      }
    }

    const totalVisits = traffic?.totals.visits ?? 0;
    const productViews = traffic?.totals.productViews ?? 0;
    const checkoutStarted = traffic?.totals.beginCheckout ?? 0;

    // Attribution rollups
    let revenueByCampaign: AttributionRollup[] = [];
    let revenueByAdset: AttributionRollup[] = [];
    let revenueByAd: AttributionRollup[] = [];
    if (orderIds.length) {
      const { data: attrs } = await anyFrom(this.supabase)('order_attribution')
        .select(
          'sales_document_id, utm_campaign, campaign_name, adset_name, ad_name, campaign_id, adset_id, ad_id',
        )
        .in('sales_document_id', orderIds);
      const attrList = (attrs ?? []) as any[];
      const orderRevenue = new Map<string, number>();
      for (const o of orders) orderRevenue.set(o.id, Number(o.total_ttc || 0));

      revenueByCampaign = rollupAttr(
        attrList,
        orderRevenue,
        (a) => a.campaign_name ?? a.utm_campaign ?? '—',
      );
      revenueByAdset = rollupAttr(
        attrList,
        orderRevenue,
        (a) => a.adset_name ?? a.adset_id ?? '—',
      );
      revenueByAd = rollupAttr(
        attrList,
        orderRevenue,
        (a) => a.ad_name ?? a.ad_id ?? '—',
      );
    }

    // Top products
    let topProducts: TopProductRollup[] = [];
    if (orderIds.length) {
      const { data: rawLines } = await anyFrom(this.supabase)(
        'sales_document_lines',
      )
        .select('document_id, article_id, description, quantity, total_ttc')
        .in('document_id', orderIds);
      const byArticle = new Map<
        string,
        { name: string; units: number; revenue: number }
      >();
      for (const l of (rawLines ?? []) as any[]) {
        const aid = l.article_id as string;
        const name = l.description as string;
        const current = byArticle.get(aid) ?? { name, units: 0, revenue: 0 };
        current.units += Number(l.quantity || 0);
        current.revenue += Number(l.total_ttc || 0);
        byArticle.set(aid, current);
      }
      topProducts = Array.from(byArticle.entries())
        .map(([productId, v]) => ({
          productId,
          productName: v.name,
          unitsSold: v.units,
          revenue: v.revenue,
        }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);
    }

    // Top categories (rough: via articles.category_id)
    let topCategories: { categoryId: string; name: string; revenue: number }[] =
      [];
    if (topProducts.length) {
      const articleIds = topProducts.map((p) => p.productId);
      // `authorized_articles` n'expose pas de relation calculée vers
      // `article_categories` : on résout le nom en une seconde requête plutôt
      // que d'ouvrir une relation de plus pour un widget.
      const { data: articles } = await anyFrom(this.supabase)(
        'authorized_articles',
      )
        .select('id, category_id')
        .in('id', articleIds);
      const categoryIds = [
        ...new Set(
          ((articles ?? []) as any[]).map((a) => a.category_id).filter(Boolean),
        ),
      ] as string[];
      const categoryNames = new Map<string, string>();
      if (categoryIds.length) {
        const { data: cats } = await anyFrom(this.supabase)(
          'authorized_article_categories',
        )
          .select('id, name')
          .in('id', categoryIds);
        for (const c of (cats ?? []) as any[])
          categoryNames.set(c.id, c.name ?? '—');
      }
      const catRevenue = new Map<string, { name: string; revenue: number }>();
      for (const p of topProducts) {
        const a = (articles ?? []).find((x: any) => x.id === p.productId);
        if (!a || !a.category_id) continue;
        const cur = catRevenue.get(a.category_id) ?? {
          name: categoryNames.get(a.category_id) ?? '—',
          revenue: 0,
        };
        cur.revenue += p.revenue;
        catRevenue.set(a.category_id, cur);
      }
      topCategories = Array.from(catRevenue.entries())
        .map(([categoryId, v]) => ({
          categoryId,
          name: v.name,
          revenue: v.revenue,
        }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);
    }

    // Revenue by city
    const byCity = new Map<string, { orders: number; revenue: number }>();
    for (const o of orders) {
      const city = o.online_customers?.city ?? '—';
      const cur = byCity.get(city) ?? { orders: 0, revenue: 0 };
      cur.orders += 1;
      cur.revenue += Number(o.total_ttc || 0);
      byCity.set(city, cur);
    }
    const revenueByCity: CityRollup[] = Array.from(byCity.entries())
      .map(([city, v]) => ({ city, orders: v.orders, revenue: v.revenue }))
      .sort((a, b) => b.revenue - a.revenue);

    // Checkout collects the city as free text, so the raw list scatters one
    // place over several spellings. The governorate rollup is what a merchant
    // actually reads "orders by location" from.
    const revenueByGovernorate: GovernorateRollup[] = rollupByGovernorate(
      revenueByCity.map((c) => ({
        city: c.city === '—' ? null : c.city,
        orders: c.orders,
        revenue: c.revenue,
      })),
    );

    // Daily orders + revenue, on the same calendar as the traffic series so the
    // two can share an axis.
    const ordersSeries = buildOrdersSeries(orders, from, to);

    // Names for the most-viewed products, so the report shows a product and not
    // a UUID. Views come from telemetry (article ids), names from the catalogue.
    if (traffic && traffic.topViewedProducts.length) {
      const ids = traffic.topViewedProducts.map((p) => p.productId);
      const { data: viewedArticles } = await anyFrom(this.supabase)(
        'authorized_articles',
      )
        .select('id, name')
        .in('id', ids);
      const nameById = new Map<string, string>(
        ((viewedArticles ?? []) as any[]).map((a) => [
          a.id as string,
          (a.name as string) ?? '',
        ]),
      );
      traffic = {
        ...traffic,
        topViewedProducts: traffic.topViewedProducts.map((p) => ({
          ...p,
          productName: nameById.get(p.productId) ?? null,
        })),
      };
    }

    // New customers in range
    const { count: newCustomers = 0 } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('online_customers')
      .select('id', { count: 'exact', head: true })
      .gte('created_at', from)
      .lte('created_at', `${to}T23:59:59`);

    // Repeat rate
    const { count: repeatCount = 0 } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('online_customers')
      .select('id', { count: 'exact', head: true })
      .gte('total_orders', 2);
    const { count: totalCust = 0 } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('online_customers')
      .select('id', { count: 'exact', head: true });
    const repeatCustomerRate = totalCust
      ? (repeatCount ?? 0) / (totalCust ?? 1)
      : 0;

    return {
      range,
      funnel: {
        visits: totalVisits,
        productViews,
        checkoutStarted,
        confirmed: totalOrders,
        delivered: deliveredOrders,
      },
      revenueByCampaign,
      revenueByAdset,
      revenueByAd,
      topProducts,
      topCategories,
      revenueByCity,
      revenueByGovernorate,
      ordersSeries,
      newCustomers: newCustomers ?? 0,
      repeatCustomerRate,
      abandonedCarts,
      totalOrders,
      totalRevenue,
      previousTotalOrders,
      previousTotalRevenue,
      traffic,
    };
  }

  /**
   * Last hour of activity. Polled by the live tab — there is no push channel
   * (Supabase Realtime is out of scope in phase 1-2), so the caller sets the
   * cadence and this stays a plain bounded query.
   *
   * Returns null rather than throwing: a live panel that can't reach the RPC
   * should read as "unavailable", not take the analytics page down with it.
   */
  async getLiveActivity(): Promise<StorefrontLiveActivity | null> {
    const { data: tenantId } = await anyRpc(this.supabase)(
      'current_user_tenant_id',
      {},
    );
    if (!tenantId) return null;

    const { data, error } = await anyRpc(this.supabase)(
      'storefront_analytics_live',
      {
        p_tenant_id: tenantId,
      },
    );
    if (error) {
      console.warn(
        '[storefront-analytics] storefront_analytics_live failed:',
        error.message,
      );
      return null;
    }
    if (!data) return null;

    const d = data as Record<string, unknown>;
    return {
      generatedAt: str(d['generatedAt']),
      activeVisitors: num(d['activeVisitors']),
      visitorsLast30: num(d['visitorsLast30']),
      visitorsLastHour: num(d['visitorsLastHour']),
      pageViewsLast30: num(d['pageViewsLast30']),
      clicksLast30: num(d['clicksLast30']),
      addToCartLast30: num(d['addToCartLast30']),
      checkoutsLast30: num(d['checkoutsLast30']),
      newVisitsLastHour: num(d['newVisitsLastHour']),
      perMinute: rows(d['perMinute']).map((r) => ({
        minute: str(r['minute']),
        pageViews: num(r['pageViews']),
        visitors: num(r['visitors']),
      })),
      activePages: rows(d['activePages']).map((r) => ({
        path: str(r['path'] ?? '/'),
        pageType: str(r['pageType'] ?? 'other'),
        visitors: num(r['visitors']),
        views: num(r['views']),
      })),
      countries: rows(d['countries']).map((r) => ({
        country: nullableStr(r['country']),
        visitors: num(r['visitors']),
      })),
      devices: rows(d['devices']).map((r) => ({
        device: str(r['device'] ?? 'other'),
        visitors: num(r['visitors']),
      })),
      feed: rows(d['feed']).map((r) => ({
        at: str(r['at']),
        eventType: str(r['eventType']),
        path: str(r['path'] ?? '/'),
        pageType: nullableStr(r['pageType']),
        element: nullableStr(r['element']),
        productId: nullableStr(r['productId']),
        country: nullableStr(r['country']),
        device: nullableStr(r['device']),
        visitor: str(r['visitor']),
      })),
      recentOrders: rows(d['recentOrders']).map((r) => ({
        at: str(r['at']),
        number: str(r['number']),
        totalTtc: num(r['totalTtc']),
        city: nullableStr(r['city']),
      })),
    };
  }
}

/** The window of equal length immediately before [from, to]. */
function previousWindow(
  from: string,
  to: string,
): { from: string; to: string } {
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  const days = Math.max(
    1,
    Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1,
  );
  const prevEnd = new Date(start.getTime() - 86_400_000);
  const prevStart = new Date(prevEnd.getTime() - (days - 1) * 86_400_000);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(prevStart), to: iso(prevEnd) };
}

/** Daily orders + revenue over [from, to], with a zero row for empty days so
 *  the chart draws a continuous line instead of jumping over gaps. */
function buildOrdersSeries(
  orders: Array<Record<string, unknown>>,
  from: string,
  to: string,
): StorefrontOrdersPoint[] {
  const byDay = new Map<string, { orders: number; revenue: number }>();
  for (const o of orders) {
    const day = String(o['doc_date'] ?? '').slice(0, 10);
    if (!day) continue;
    const cur = byDay.get(day) ?? { orders: 0, revenue: 0 };
    cur.orders += 1;
    cur.revenue += Number(o['total_ttc'] || 0);
    byDay.set(day, cur);
  }
  const out: StorefrontOrdersPoint[] = [];
  const cursor = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  // Guard against a nonsense range producing an unbounded loop.
  for (let i = 0; cursor <= end && i < 800; i += 1) {
    const day = cursor.toISOString().slice(0, 10);
    const hit = byDay.get(day);
    out.push({
      date: day,
      orders: hit?.orders ?? 0,
      revenue: round3(hit?.revenue ?? 0),
    });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const str = (v: unknown): string =>
  typeof v === 'string' ? v : String(v ?? '');
const nullableStr = (v: unknown): string | null =>
  v === null || v === undefined || v === '' ? null : String(v);
const rows = (v: unknown): Array<Record<string, unknown>> =>
  Array.isArray(v) ? (v as Array<Record<string, unknown>>) : [];

/** Map the `storefront_analytics_traffic` JSONB payload onto the domain type.
 *  Every field is defensive: an older function version that predates a key
 *  should degrade to an empty section, not crash the dashboard. */
function mapTraffic(
  raw: unknown,
  fallback: {
    from: string;
    to: string;
    previous: { from: string; to: string };
  },
): StorefrontTraffic {
  const d = (raw ?? {}) as Record<string, unknown>;
  const rangeRaw = (d['range'] ?? {}) as Record<string, unknown>;
  const totals = (d['totals'] ?? {}) as Record<string, unknown>;
  const prev = (d['previous'] ?? {}) as Record<string, unknown>;

  return {
    range: {
      from: str(rangeRaw['from'] ?? fallback.from),
      to: str(rangeRaw['to'] ?? fallback.to),
      previousFrom: str(rangeRaw['previousFrom'] ?? fallback.previous.from),
      previousTo: str(rangeRaw['previousTo'] ?? fallback.previous.to),
      days: num(rangeRaw['days']),
      timezone: str(rangeRaw['timezone'] ?? 'Africa/Tunis'),
    },
    totals: {
      visits: num(totals['visits']),
      uniqueVisitors: num(totals['uniqueVisitors']),
      pageViews: num(totals['pageViews']),
      clicks: num(totals['clicks']),
      productViews: num(totals['productViews']),
      addToCart: num(totals['addToCart']),
      beginCheckout: num(totals['beginCheckout']),
      pageSessions: num(totals['pageSessions']),
      bouncedSessions: num(totals['bouncedSessions']),
    },
    previous: {
      visits: num(prev['visits']),
      uniqueVisitors: num(prev['uniqueVisitors']),
      pageViews: num(prev['pageViews']),
      clicks: num(prev['clicks']),
      productViews: num(prev['productViews']),
      addToCart: num(prev['addToCart']),
      beginCheckout: num(prev['beginCheckout']),
    },
    series: rows(d['series']).map((r) => ({
      date: str(r['date']).slice(0, 10),
      visits: num(r['visits']),
      uniqueVisitors: num(r['uniqueVisitors']),
      pageViews: num(r['pageViews']),
      clicks: num(r['clicks']),
      productViews: num(r['productViews']),
      addToCart: num(r['addToCart']),
    })),
    hourly: rows(d['hourly']).map((r) => ({
      hour: num(r['hour']),
      visits: num(r['visits']),
    })),
    referrers: rows(d['referrers']).map((r) => ({
      type: str(r['type'] ?? 'direct'),
      visits: num(r['visits']),
      uniqueVisitors: num(r['uniqueVisitors']),
    })),
    devices: rows(d['devices']).map((r) => ({
      device: str(r['device'] ?? 'other'),
      visits: num(r['visits']),
    })),
    browsers: rows(d['browsers']).map((r) => ({
      browser: str(r['browser'] ?? 'other'),
      visits: num(r['visits']),
    })),
    operatingSystems: rows(d['operatingSystems']).map((r) => ({
      os: str(r['os'] ?? 'other'),
      visits: num(r['visits']),
    })),
    countries: rows(d['countries']).map((r) => ({
      country: nullableStr(r['country']),
      visits: num(r['visits']),
      uniqueVisitors: num(r['uniqueVisitors']),
    })),
    utmCampaigns: rows(d['utmCampaigns']).map((r) => ({
      campaign: str(r['campaign']),
      source: nullableStr(r['source']),
      medium: nullableStr(r['medium']),
      visits: num(r['visits']),
    })),
    utmSources: rows(d['utmSources']).map((r) => ({
      source: str(r['source']),
      visits: num(r['visits']),
    })),
    landingPages: rows(d['landingPages']).map((r) => ({
      path: str(r['path'] ?? '/'),
      visits: num(r['visits']),
      uniqueVisitors: num(r['uniqueVisitors']),
    })),
    topPages: rows(d['topPages']).map((r) => ({
      path: str(r['path'] ?? '/'),
      views: num(r['views']),
      uniqueVisitors: num(r['uniqueVisitors']),
    })),
    pageTypes: rows(d['pageTypes']).map((r) => ({
      type: str(r['type'] ?? 'other'),
      views: num(r['views']),
      uniqueVisitors: num(r['uniqueVisitors']),
    })),
    topClicks: rows(d['topClicks']).map((r) => ({
      element: str(r['element']),
      label: nullableStr(r['label']),
      clicks: num(r['clicks']),
      uniqueVisitors: num(r['uniqueVisitors']),
    })),
    topClickTargets: rows(d['topClickTargets']).map((r) => ({
      element: str(r['element']),
      label: str(r['label']),
      href: nullableStr(r['href']),
      clicks: num(r['clicks']),
    })),
    topViewedProducts: mergeAddToCart(
      rows(d['topViewedProducts']),
      rows(d['addToCartByProduct']),
    ),
  };
}

/** Zip product views with the add-to-cart count for the same product, so the
 *  report can show which product page converts rather than only which is busy. */
function mergeAddToCart(
  viewed: Array<Record<string, unknown>>,
  addToCart: Array<Record<string, unknown>>,
): StorefrontViewedProductRow[] {
  const cartByProduct = new Map<string, number>();
  for (const r of addToCart) {
    const id = str(r['productId']);
    if (id) cartByProduct.set(id, num(r['count']));
  }
  return viewed.map((r) => {
    const productId = str(r['productId']);
    return {
      productId,
      productName: null,
      views: num(r['views']),
      uniqueVisitors: num(r['uniqueVisitors']),
      addToCart: cartByProduct.get(productId) ?? 0,
    };
  });
}

function rollupAttr(
  attrs: Array<Record<string, unknown>>,
  revenue: Map<string, number>,
  keyFn: (a: any) => string,
): AttributionRollup[] {
  const out = new Map<string, { orders: number; revenue: number }>();
  for (const a of attrs) {
    const orderId = a['sales_document_id'] as string;
    const rev = revenue.get(orderId) ?? 0;
    const key = keyFn(a) || '—';
    const cur = out.get(key) ?? { orders: 0, revenue: 0 };
    cur.orders += 1;
    cur.revenue += rev;
    out.set(key, cur);
  }
  return Array.from(out.entries())
    .map(([key, v]) => ({
      key,
      label: key,
      orders: v.orders,
      revenue: v.revenue,
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);
}
